import React from 'react';
import Latex from 'react-latex-next';
import { motion } from 'motion/react';
import { formatLatexString } from '@/lib/latexUtils';
import { Step } from '@/lib/gemini';
import { normalizeBoardContentLines, isRealMarkdownTableRow } from '@/lib/layoutEngine';

export interface StructuredSolutionAreaProps {
  step: Step | null | undefined;
  fontSizeClass?: string;
  lineGapClass?: string;
  isExport?: boolean;
  stepIndex?: number;
  className?: string;
}

export interface ParsedBlock {
  id: string;
  type: 'table' | 'bullet' | 'heading' | 'matrix' | 'aligned' | 'statement' | 'svg';
  raw: string;
  tableData?: {
    headers: string[];
    rows: string[][];
  };
  svgContent?: string;
}

/**
 * Parses raw board content and structured step data into cohesive educational visual blocks.
 * Groups multi-line markdown tables, detects matrices, aligned derivations, and headings.
 */
export function parseBoardContentBlocks(content: string, step?: Step | null): ParsedBlock[] {
  if (!content && !step?.diagram_svg) return [];

  const rawLines = normalizeBoardContentLines(content || '');
  const blocks: ParsedBlock[] = [];
  let tableAccumulator: string[] = [];

  const flushTable = () => {
    if (tableAccumulator.length === 0) return;
    
    // Parse table rows
    const parsedRows: string[][] = [];
    for (const tLine of tableAccumulator) {
      const rawCells = tLine.split('|').map(c => c.trim());
      const cells = rawCells.filter((c, idx) => {
        if ((idx === 0 || idx === rawCells.length - 1) && c === '') return false;
        return true;
      });
      // Skip separator rows like |---|---|
      if (cells.length > 0 && !cells.every(c => /^[-:\s]+$/.test(c))) {
        parsedRows.push(cells);
      }
    }

    if (parsedRows.length > 0) {
      const headers = parsedRows.length > 1 ? parsedRows[0] : [];
      const rows = parsedRows.length > 1 ? parsedRows.slice(1) : parsedRows;
      blocks.push({
        id: `block-${blocks.length + 1}`,
        type: 'table',
        raw: tableAccumulator.join('\n'),
        tableData: {
          headers,
          rows
        }
      });
    }
    tableAccumulator = [];
  };

  for (let i = 0; i < rawLines.length; i++) {
    const line = rawLines[i].trim();
    if (!line) continue;

    // 1. Table row detection using strict markdown table syntax (avoids math determinants)
    const isTableRow = isRealMarkdownTableRow(line);
    if (isTableRow) {
      tableAccumulator.push(line);
      continue;
    } else {
      flushTable();
    }

    // 2. Matrix or Determinant environment detection
    if (/\\begin\{(pmatrix|bmatrix|vmatrix|matrix)\}/i.test(line)) {
      blocks.push({
        id: `block-${blocks.length + 1}`,
        type: 'matrix',
        raw: line
      });
      continue;
    }

    // 3. Aligned Derivation detection
    if (/\\begin\{(aligned|cases|split)\}/i.test(line)) {
      blocks.push({
        id: `block-${blocks.length + 1}`,
        type: 'aligned',
        raw: line
      });
      continue;
    }

    // 4. Heading detection
    const cleanHeaderCand = line.replace(/^#{1,6}\s+/, '').replace(/\*\*/g, '').trim();
    const isHeading = /^(GIVEN DATA|TO FIND|TO PROVE|GOAL|OBJECTIVE|WHAT TO FIND|WHAT TO PROVE|STEP \d+|EXAMPLE CALCULATION|FORMULA|KEY CONCEPT|CONCLUSION|METHOD|CASE \d+|DEFINITION|FINDING|FINAL CALCULATION|CALCULATION|SOLUTION|ANSWER|RESULT)/i.test(cleanHeaderCand) || 
      (/^#{1,4}\s+/.test(line) && !line.includes('=')) ||
      (cleanHeaderCand.endsWith(':') && !cleanHeaderCand.includes('=') && cleanHeaderCand.length < 60);

    if (isHeading) {
      if (cleanHeaderCand.includes(':')) {
        const colonIdx = cleanHeaderCand.indexOf(':');
        const headerPart = cleanHeaderCand.slice(0, colonIdx).trim();
        const restPart = cleanHeaderCand.slice(colonIdx + 1).trim();

        blocks.push({
          id: `block-${blocks.length + 1}`,
          type: 'heading',
          raw: headerPart
        });

        if (restPart) {
          const isBulletPart = /^([-*•]|\d+\.)\s+/.test(restPart);
          blocks.push({
            id: `block-${blocks.length + 1}`,
            type: 'bullet',
            raw: isBulletPart ? restPart : `• ${restPart}`
          });
        }
        continue;
      }

      blocks.push({
        id: `block-${blocks.length + 1}`,
        type: 'heading',
        raw: cleanHeaderCand
      });
      continue;
    }

    // 5. Bullet detection
    const bulletMatch = line.match(/^([-*•]|\d+\.)\s+(.*)/);
    if (bulletMatch) {
      blocks.push({
        id: `block-${blocks.length + 1}`,
        type: 'bullet',
        raw: line
      });
      continue;
    }

    // 6. Standard statement / equation
    blocks.push({
      id: `block-${blocks.length + 1}`,
      type: 'statement',
      raw: line
    });
  }

  flushTable();
  return blocks;
}

export const StructuredSolutionArea: React.FC<StructuredSolutionAreaProps> = ({
  step,
  fontSizeClass,
  lineGapClass,
  isExport = false,
  stepIndex = 0,
  className = ''
}) => {
  const content = step?.board_content || '';
  const blocks = parseBoardContentBlocks(content, step);

  if (blocks.length === 0 && !step?.reference_text) {
    return null;
  }

  // Calculate dynamic typography and spacing based on density and block types
  const totalRawLength = blocks.reduce((acc, b) => acc + b.raw.length, 0);
  const hasTallContent = blocks.some(b => 
    b.type === 'table' || 
    b.type === 'matrix' || 
    b.type === 'aligned' || 
    /\\frac\{|\\sum|\\int|\\prod|\\begin/.test(b.raw)
  );

  let activeFontSize = fontSizeClass;
  let activeLineGap = lineGapClass;

  if (!activeFontSize) {
    if (blocks.length >= 4 || (blocks.length >= 3 && hasTallContent) || totalRawLength > 200) {
      activeFontSize = 'text-xl md:text-2xl';
      activeLineGap = 'gap-2 md:gap-3';
    } else if (blocks.length >= 3 || hasTallContent || totalRawLength > 120) {
      activeFontSize = 'text-2xl md:text-3xl';
      activeLineGap = 'gap-3 md:gap-4';
    } else {
      activeFontSize = 'text-3xl md:text-4xl';
      activeLineGap = 'gap-4 md:gap-6';
    }
  } else if (!activeLineGap) {
    activeLineGap = 'gap-3 md:gap-4';
  }

  const containerRef = React.useRef<HTMLDivElement>(null);
  const [scale, setScale] = React.useState<number>(1.0);

  React.useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const measureFit = () => {
      const parent = el.parentElement;
      if (!parent) return;
      const availableH = parent.clientHeight;
      const contentH = el.scrollHeight;

      if (availableH > 0 && contentH > availableH) {
        // Calculate scale down ratio to guarantee zero boundary overflow
        const ratio = (availableH - 8) / contentH;
        setScale(Math.max(0.72, Math.min(1.0, ratio)));
      } else {
        setScale(1.0);
      }
    };

    measureFit();
    const timer = setTimeout(measureFit, 60);
    return () => clearTimeout(timer);
  }, [step, blocks, activeFontSize, activeLineGap]);

  return (
    <div
      ref={containerRef}
      style={{
        transform: scale < 1.0 ? `scale(${scale})` : undefined,
        transformOrigin: 'top left',
        width: scale < 1.0 ? `${100 / scale}%` : '100%',
        transition: 'transform 0.15s ease-out'
      }}
      className={`w-full flex flex-col ${activeLineGap} justify-start items-start overflow-hidden max-h-full ${className}`}
    >
      {blocks.map((block, idx) => {
        // 1. STRUCTURED TABLE REPRESENTATION
        if (block.type === 'table' && block.tableData) {
          const { headers, rows } = block.tableData;
          return (
            <div
              key={`${stepIndex}-table-${idx}`}
              className="w-full my-2 overflow-x-auto rounded-2xl bg-slate-900/95 border border-cyan-500/40 shadow-xl p-1"
            >
              <table className="w-full border-collapse text-left">
                {headers.length > 0 && (
                  <thead>
                    <tr className="bg-cyan-950/80 border-b border-cyan-500/40">
                      {headers.map((h, hIdx) => (
                        <th
                          key={hIdx}
                          className="px-4 py-2 text-center text-cyan-200 font-extrabold text-sm md:text-base uppercase tracking-wider border-r last:border-r-0 border-cyan-800/40"
                        >
                          <Latex strict={false}>{formatLatexString(h, true)}</Latex>
                        </th>
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody>
                  {rows.map((row, rIdx) => (
                    <tr
                      key={rIdx}
                      className={`border-b last:border-b-0 border-slate-800/80 ${
                        rIdx % 2 === 0 ? 'bg-slate-900/60' : 'bg-slate-950/70'
                      }`}
                    >
                      {row.map((cell, cIdx) => (
                        <td
                          key={cIdx}
                          className="px-4 py-2 text-center text-slate-100 font-bold text-sm md:text-base border-r last:border-r-0 border-slate-800/60 whitespace-normal break-words"
                        >
                          <Latex strict={false}>{formatLatexString(cell, true)}</Latex>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        // 2. MATRIX / DETERMINANT REPRESENTATION
        if (block.type === 'matrix') {
          return (
            <div
              key={`${stepIndex}-matrix-${idx}`}
              className="w-full my-1.5 py-2.5 px-5 rounded-2xl bg-slate-900/90 border border-indigo-500/40 flex items-center justify-start overflow-x-auto shadow-lg"
            >
              <div className={`${activeFontSize} font-bold text-white tracking-wide leading-relaxed`}>
                <Latex strict={false}>{formatLatexString(block.raw, false)}</Latex>
              </div>
            </div>
          );
        }

        // 3. ALIGNED DERIVATION REPRESENTATION
        if (block.type === 'aligned') {
          return (
            <div
              key={`${stepIndex}-aligned-${idx}`}
              className="w-full my-1.5 py-2.5 px-5 rounded-2xl bg-slate-900/90 border border-cyan-500/40 flex items-center justify-start overflow-x-auto shadow-lg"
            >
              <div className={`${activeFontSize} font-bold text-white leading-relaxed tracking-wide`}>
                <Latex strict={false}>{formatLatexString(block.raw, false)}</Latex>
              </div>
            </div>
          );
        }

        // 4. HEADING REPRESENTATION
        if (block.type === 'heading') {
          const cleanHeadingText = block.raw.replace(/^#{1,6}\s+/, '').replace(/\*\*/g, '').replace(/:$/, '').trim();
          return (
            <div
              key={`${stepIndex}-heading-${idx}`}
              className="w-full mt-0.5 mb-0.5 text-left"
            >
              <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-xl bg-cyan-950/80 border border-cyan-500/50 text-cyan-300 font-black text-xs md:text-sm uppercase tracking-widest shadow-md">
                <Latex strict={false}>{formatLatexString(cleanHeadingText, true)}</Latex>
              </div>
            </div>
          );
        }

        // 5. BULLET LIST ITEM
        if (block.type === 'bullet') {
          const bulletMatch = block.raw.match(/^([-*•]|\d+\.)\s+(.*)/);
          const prefix = bulletMatch ? bulletMatch[1] : '•';
          const contentText = (bulletMatch ? bulletMatch[2] : block.raw).replace(/^#{1,6}\s+/, '').replace(/\*\*/g, '').trim();

          return (
            <div
              key={`${stepIndex}-bullet-${idx}`}
              className="flex items-start gap-3 my-0.5 text-left w-full px-1"
            >
              <span className="text-cyan-400 font-black text-lg md:text-xl flex-shrink-0 mt-0.5">
                {/^\d+\./.test(prefix) ? prefix : '•'}
              </span>
              <div className={`flex-1 text-[#f8fafc] ${activeFontSize} font-bold tracking-tight leading-relaxed break-words`}>
                <Latex strict={false}>{formatLatexString(contentText, true)}</Latex>
              </div>
            </div>
          );
        }

        // 6. GENERAL STATEMENT / EQUATION
        const cleanStmt = block.raw.replace(/^#{1,6}\s+/, '').trim();
        return (
          <div
            key={`${stepIndex}-stmt-${idx}`}
            className={`text-[#f8fafc] ${activeFontSize} font-bold tracking-tight text-left leading-relaxed w-full px-1 break-words`}
          >
            <Latex strict={false}>{formatLatexString(cleanStmt, false)}</Latex>
          </div>
        );
      })}

      {/* Visual Reference connector line / badge */}
      {step?.reference_text && (
        <div className="mt-2 flex items-center gap-2.5 px-3.5 py-1.5 rounded-xl bg-rose-950/60 border border-rose-500/40 text-rose-200 font-medium text-xs md:text-sm leading-tight w-full shadow-sm">
          <span className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg bg-rose-500/20 text-rose-400 font-bold text-xs uppercase tracking-wider flex-shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
            Reference
          </span>
          <span className="flex-1 truncate">
            <Latex strict={false}>{formatLatexString(step.reference_text, true)}</Latex>
          </span>
        </div>
      )}
    </div>
  );
};
